<template>
    <!-- UI-05B - redesign SOLO visivo della drill modal della heatmap.
         Logica, dati e comportamento sono quelli gia' validati: nessuna
         ricalcolazione, nessun nuovo dato, export invariati. -->
    <div v-if="aperto" class="modal fade show ui05-modal ui07-modal" tabindex="-1" role="dialog"
         style="display: block; background: rgba(0, 0, 0, 0.5);">
        <div class="modal-dialog modal-xl modal-fullscreen-sm-down" role="document">
            <div class="modal-content">

                <!-- 1. HEADER: testata colorata (UI-05C). Geometria INVARIATA:
                     nessuna altezza cambiata, solo il colore di fondo e del testo. -->
                <div class="modal-header ui07-header">
                    <div class="ui07-header-riga">
                        <h5 class="modal-title ui07-titolo mb-0">{{ nomeMeseCompleto }}</h5>
                        <span class="ui07-pill">{{ etichettaMetrica }}</span>
                        <span v-if="periodo" class="ui07-periodo">{{ periodo }}</span>
                    </div>
                    <button type="button" class="btn-close btn-close-white" @click="chiudi"
                            aria-label="Chiudi"></button>
                </div>

                <div class="modal-body ui07-body">
                    <div v-if="caricamento" class="text-center py-4">
                        <div class="spinner-border spinner-border-sm me-2" role="status"></div>
                        Caricamento dei progetti del mese…
                    </div>

                    <div v-else-if="errore" class="alert alert-warning mb-0" role="alert">{{ errore }}</div>

                    <template v-else-if="dati">
                        <!-- 2. 6 KPI COMPATTI, una riga su desktop (xl). -->
                        <div class="row g-2 ui07-kpi-row">
                            <div v-for="k in kpiScheda" :key="k.chiave" class="col-6 col-md-4 col-xl-2">
                                <div class="ui07-kpi" :class="[k.tinta, { 'ui07-kpi-attivo': k.attivo }]">
                                    <div class="ui07-kpi-riga">
                                        <i class="fas ui07-kpi-icona" :class="k.icona" aria-hidden="true"></i>
                                        <span class="ui07-kpi-label">{{ k.label }}</span>
                                    </div>
                                    <div class="ui07-kpi-valore">{{ k.valore }}</div>
                                </div>
                            </div>
                        </div>
                        <!-- 3. CONFRONTO con l'anno precedente: card bassa, una riga. -->
                        <div class="ui07-confronto">
                            <div class="ui07-blocco-titolo">Confronto con {{ nomeMesePrecedente }}</div>
                            <div class="row g-2 ui07-confronto-riga">
                                <div class="col-6 col-md-3">
                                    <div class="ui07-micro-label">{{ nomeMeseCompleto }}</div>
                                    <div class="ui07-micro-valore">{{ confronto.corrente }}</div>
                                </div>
                                <div class="col-6 col-md-3">
                                    <div class="ui07-micro-label">{{ nomeMesePrecedente }}</div>
                                    <div class="ui07-micro-valore">{{ confronto.precedente }}</div>
                                </div>
                                <div class="col-6 col-md-3">
                                    <div class="ui07-micro-label">Variazione assoluta</div>
                                    <div class="ui07-var" :class="classeVariazione">{{ confronto.differenza }}</div>
                                </div>
                                <div class="col-12 col-md-3">
                                    <div class="ui07-micro-label">Variazione percentuale</div>
                                    <template v-if="confronto.percentuale">
                                        <div class="ui07-var" :class="classeVariazione">
                                            {{ confronto.percentuale }}
                                        </div>
                                    </template>
                                    <!-- INFO BOX azzurro: precedente = 0, la percentuale
                                         NON e' definita. Nessun +100% inventato. -->
                                    <div v-else class="ui07-infobox">
                                        <i class="fas fa-info-circle ui07-infobox-icona" aria-hidden="true"></i>
                                        <span>{{ confronto.nota }}</span>
                                    </div>
                                </div>
                            </div>
                        </div>

                        <!-- 4. COMPOSIZIONE DEL MESE: conteggio + totale della metrica. -->
                        <div class="ui07-composizione">
                            <span class="ui07-blocco-titolo mb-0">Composizione del mese</span>
                            <span class="ui07-composizione-meta">
                                {{ progetti.length }} {{ progetti.length === 1 ? "progetto" : "progetti" }}
                                &middot; {{ etichettaMetrica }} totale {{ totaleMetrica }}
                            </span>
                            <button v-if="progetti.length > 1" type="button"
                                    class="btn btn-link btn-sm p-0 ui07-link-progetti"
                                    @click="vaiAComposizione">
                                Vai alla tabella ↓
                            </button>
                        </div>
                        <div v-if="!progetti.length" class="text-muted py-4 text-center">
                            Nessun progetto contribuisce a questo mese.
                        </div>

                        <!-- 5-6. TABELLA: area scrollabile PROPRIA (max-height basata sul
                                 viewport), thead sticky e totale sticky in basso: la modale
                                 non cresce con i progetti e restano ~5 righe visibili.
                                 Nessuna colonna "azione": non esiste una route di
                                 dettaglio progetto verificata e sicura. -->
                        <div v-else class="table-responsive ui07-tabella-wrap" ref="zonaTabella">
                            <table class="table table-sm ui07-tabella mb-0 align-middle">
                                <caption class="visually-hidden">Progetti del mese con i valori economici</caption>
                                <thead>
                                    <tr>
                                        <th scope="col" class="ui07-col-idx">#</th>
                                        <th scope="col">Progetto</th>
                                        <th scope="col">Cliente</th>
                                        <th scope="col">Stato</th>
                                        <th scope="col" class="text-end" :class="colonnaAttiva('fatturato')">Fatturato</th>
                                        <th scope="col" class="text-end" :class="colonnaAttiva('costi')">Costi</th>
                                        <th scope="col" class="text-end" :class="colonnaAttiva('margine')">Margine</th>
                                        <th scope="col" class="text-end" :class="colonnaAttiva('margine_percent')">Margine %</th>
                                    </tr>
                                </thead>
                                <tbody>
                                    <tr v-for="(p, i) in progetti" :key="p.project_id">
                                        <td class="text-muted ui07-col-idx">{{ i + 1 }}</td>
                                        <th scope="row">
                                            <div class="ui07-progetto">{{ p.title || "(senza titolo)" }}</div>
                                            <div class="ui07-codice">{{ p.code }}</div>
                                        </th>
                                        <td>{{ p.cliente }}</td>
                                        <td><span class="ui07-stato" :class="classeStato(p.state)">{{ p.state || "—" }}</span></td>
                                        <td class="text-end" :class="colonnaAttiva('fatturato')">{{ p.fatturatoEur }}</td>
                                        <td class="text-end" :class="colonnaAttiva('costi')">{{ p.costiEur }}</td>
                                        <td class="text-end" :class="colonnaAttiva('margine')">{{ p.margineEur }}</td>
                                        <td class="text-end" :class="colonnaAttiva('margine_percent')">{{ p.marginePercent }}</td>
                                    </tr>
                                </tbody>
                                <!-- 8. TOTALE: sticky in basso, sempre raggiungibile. -->
                                <tfoot>
                                    <tr>
                                        <td colspan="4" class="text-end ui07-totale-label">TOTALE</td>
                                        <td class="text-end ui07-totale" :class="colonnaAttiva('fatturato')">{{ totale.fatturatoEur }}</td>
                                        <td class="text-end ui07-totale" :class="colonnaAttiva('costi')">{{ totale.costiEur }}</td>
                                        <td class="text-end ui07-totale" :class="colonnaAttiva('margine')">{{ totale.margineEur }}</td>
                                        <td class="text-end ui07-totale" :class="colonnaAttiva('margine_percent')">{{ totale.marginePercent }}</td>
                                    </tr>
                                </tfoot>
                            </table>
                        </div>
                    </template>
                </div>

                <!-- 9. FOOTER compatto, azioni allineate a destra.
                     Gli export NON sono stati toccati (report 1041). -->
                <div class="modal-footer ui07-footer">
                    <button type="button" class="btn btn-sm btn-outline-secondary ui07-btn"
                            :disabled="!dati" @click="esporta(2)">
                        <i class="fas fa-file-excel me-1" aria-hidden="true"></i>Esporta Excel
                    </button>
                    <button type="button" class="btn btn-sm btn-outline-secondary ui07-btn"
                            :disabled="!dati" @click="esporta(1)">
                        <i class="fas fa-file-pdf me-1" aria-hidden="true"></i>Esporta PDF
                    </button>
                    <button type="button" class="btn btn-sm btn-primary ui07-btn" @click="chiudi">Chiudi</button>
                </div>
            </div>
        </div>
    </div>
</template>
<script>
/* UI-05B - redesign SOLO visivo della Drill Modal del mese.
 *
 * LOGICA INVARIATA (validata in UI-07): nessun ricalcolo economico, nessun nuovo
 * dato. I valori arrivano dall'endpoint /theb/dashboard/manager/heatmap/month,
 * che riusa perimetroSql() e v1DaRiga() (stesse formule di KPI, trend, heatmap)
 * con gli STESSI filtri e scope della Dashboard. Gli export puntano al report
 * 1041, invariati.
 *
 * AGGIUNTE (solo presentazione): nome completo del mese, icone/colori dei KPI,
 * label del totale sulla metrica selezionata, scroll interno della tabella.
 */
const FORMAT_EUR = new Intl.NumberFormat("it-IT", {
    style: "currency", currency: "EUR", minimumFractionDigits: 0, maximumFractionDigits: 0,
});

/* Etichette e tinti dei KPI: puramente presentazione. */
const ETICHETTE_METRICA = { fatturato: "Fatturato", costi: "Costi", margine: "Margine", budget: "Budget" };

/* Nomi MESI COMPLETI in italiano: il backend espone "gen 2026", qui si vuole
   "Gennaio 2026". Derivato dal mese (YYYY-MM), nessun dato dal backend cambia. */
const MESI_IT = ["Gennaio", "Febbraio", "Marzo", "Aprile", "Maggio", "Giugno",
    "Luglio", "Agosto", "Settembre", "Ottobre", "Novembre", "Dicembre"];

/* UI-05C - tinte delle colonne metriche. La tabella ESPONE Fatturato, Costi,
   Margine e Margine %: Budget non e' una colonna (resta solo KPI), quindi non
   ha una tinta di colonna. */
const TINTE_COLONNA = {
    fatturato: "ui07-col-blu",
    costi: "ui07-col-verde",
    margine: "ui07-col-arancio",
    margine_percent: "ui07-col-viola",
};

export default {
    name: "HeatmapDrillModal",

    props: {
        aperto: { type: Boolean, default: false },
        mese: { type: String, default: "" },        // YYYY-MM
        metrica: { type: String, default: "fatturato" },
        periodo: { type: String, default: "" },      // etichetta periodo dashboard
        filtri: { type: Object, default: null },     // stessi filtri della dashboard
    },

    data() {
        return { dati: null, caricamento: false, errore: "" };
    },

    computed: {
        /* "Gennaio 2026" invece di "gen 2026". */
        nomeMeseCompleto() {
            return this.nomeMeseDa(this.mese) || (this.dati && this.dati.etichetta) || "";
        },
        nomeMesePrecedente() {
            const p = this.dati && this.dati.mese_prec ? this.dati.mese_prec.slice(0, 7) : "";
            return this.nomeMeseDa(p) || "";
        },
        etichettaMetrica() {
            return ETICHETTE_METRICA[this.metrica] || this.metrica;
        },
        /* Totale della metrica selezionata, per la riga "Composizione del mese". */
        totaleMetrica() {
            return FORMAT_EUR.format(this.valoreMetrica(this.kpi));
        },
        kpi() {
            return (this.dati && this.dati.kpi) || {};
        },
        progetti() {
            if (!this.dati || !Array.isArray(this.dati.progetti)) return [];
            return this.dati.progetti.map((p) => Object.assign({}, p, {
                fatturatoEur: FORMAT_EUR.format(p.fatturato),
                costiEur: FORMAT_EUR.format(p.costi),
                margineEur: FORMAT_EUR.format(p.margine),
                marginePercent: this.percentuale(p.margine_percent),
            }));
        },
        totale() {
            const k = this.kpi;
            return {
                fatturatoEur: FORMAT_EUR.format(k.fatturato || 0),
                costiEur: FORMAT_EUR.format(k.costi || 0),
                margineEur: FORMAT_EUR.format(k.margine || 0),
                /* Margine % del TOTALE, non media delle percentuali dei progetti:
                   stessa regola di v1DaRiga() e del KPI di dashboard. */
                marginePercent: this.percentuale(k.margine_percent),
            };
        },
        kpiScheda() {
            const k = this.kpi;
            const n = this.progetti.length;
            return [
                { chiave: "fatturato", label: "Fatturato", icona: "fa-euro-sign", tinta: "ui07-t-blu",
                  valore: FORMAT_EUR.format(k.fatturato || 0), attivo: this.metrica === "fatturato" },
                { chiave: "costi", label: "Costi", icona: "fa-coins", tinta: "ui07-t-verde",
                  valore: FORMAT_EUR.format(k.costi || 0), attivo: this.metrica === "costi" },
                { chiave: "margine", label: "Margine", icona: "fa-chart-line", tinta: "ui07-t-arancio",
                  valore: FORMAT_EUR.format(k.margine || 0), attivo: this.metrica === "margine" },
                { chiave: "margine_percent", label: "Margine %", icona: "fa-percent", tinta: "ui07-t-viola",
                  valore: this.percentuale(k.margine_percent), attivo: false },
                { chiave: "budget", label: "Budget", icona: "fa-briefcase", tinta: "ui07-t-teal",
                  valore: FORMAT_EUR.format(k.budget || 0), attivo: this.metrica === "budget" },
                { chiave: "progetti", label: "Progetti", icona: "fa-layer-group", tinta: "ui07-t-slate",
                  valore: n === 1 ? "1 progetto" : (n + " progetti"), attivo: false },
            ];
        },
        /* Confronto con l'anno precedente, sulla metrica selezionata. */
        confronto() {
            const prec = (this.dati && this.dati.precedente) || {};
            const val = this.valoreMetrica(this.kpi);
            const valPrec = this.valoreMetrica(prec);
            const diff = val - valPrec;
            const segno = diff > 0 ? "+" : (diff < 0 ? "−" : "");
            const out = {
                corrente: FORMAT_EUR.format(val),
                precedente: FORMAT_EUR.format(valPrec),
                differenza: segno + FORMAT_EUR.format(Math.abs(diff)),
                percentuale: "",
                nota: "",
            };
            if (valPrec === 0) {
                /* Precedente = 0: la percentuale NON e' definita, quindi niente
                   "+100%" inventato. Si dichiara la differenza e il limite. */
                out.nota = val === 0
                    ? "Nessuna variazione: entrambi i valori sono zero."
                    : "Variazione percentuale non calcolabile: il valore dell'anno precedente è pari a zero.";
            } else {
                const p = (diff / Math.abs(valPrec)) * 100;
                out.percentuale = (p > 0 ? "+" : (p < 0 ? "−" : "")) + Math.abs(p).toFixed(1) + " %";
            }
            return out;
        },
        /* Semantica del colore: positivo verde, negativo rosso, neutro grigio.
           NOTA: per COSTI una crescita non e' un buon risultato, quindi su
           quella metrica il semaforo non viene applicato (test esistente). */
        classeVariazione() {
            const diff = this.valoreMetrica(this.kpi) - this.valoreMetrica(this.dati ? this.dati.precedente : {});
            if (diff === 0) return "ui07-neutro";
            if (this.metrica === "costi") return "ui07-neutro";
            return diff > 0 ? "ui07-positivo" : "ui07-negativo";
        },
    },

    watch: {
        aperto(val) { if (val) this.carica(); },
        /* Cambio metrica nella heatmap: il titolo e' metrica-dipendente. */
        metrica() { if (this.aperto) this.carica(); },
    },

    methods: {
        /* UI-05C: classe colore della COLONNA corrispondente alla metrica che ha
           aperto la modale. Nessuna duplicazione di template: una sola funzione,
           valore "" quando la colonna non e' quella attiva. */
        colonnaAttiva(chiave) {
            if (this.metrica !== chiave) return "";
            return "ui07-col-attiva " + this.tintaColonna(chiave);
        },
        tintaColonna(chiave) {
            return TINTE_COLONNA[chiave] || "";
        },
        /* Stato progetto: SOLO due etichette realmente note al sistema
           (COMPLETATO, richiesto dal requisito; CHIUSO, la classe CHIUSO del
           contract stati()). Nessun mapping inventato: gli altri stati restano
           neutro. Il VALORE dello stato non viene mai modificato. */
        classeStato(stato) {
            const s = String(stato || "").trim().toLowerCase();
            if (s === "completato" || s === "chiuso") return "ui07-stato-chiuso";
            return "";
        },
        /* "2026-01" -> "Gennaio 2026". Nessun accesso al backend. */
        nomeMeseDa(ym) {
            if (!ym || !/^\d{4}-\d{2}$/.test(String(ym))) return "";
            const i = parseInt(String(ym).slice(5, 7), 10) - 1;
            if (i < 0 || i > 11) return "";
            return MESI_IT[i] + " " + String(ym).slice(0, 4);
        },
        percentuale(v) {
            const n = (typeof v === "number" && isFinite(v)) ? v : 0;
            return n.toFixed(1).replace(".", ",") + " %";
        },
        valoreMetrica(src) {
            if (!src) return 0;
            const v = src[this.metrica];
            return (typeof v === "number" && isFinite(v)) ? v : 0;
        },
        chiudi() { this.$emit("chiudi"); },
        /* Porta alla tabella: scroll interno controllato, NESSUNA modale annidata. */
        vaiAComposizione() {
            const el = this.$refs.zonaTabella;
            if (el && el.scrollIntoView) el.scrollIntoView({ behavior: "smooth", block: "start" });
        },
        /* Parametri del drill: gli STESSI filtri della dashboard + il mese. */
        parametri(extra) {
            const params = Object.assign({}, this.filtri || {}, extra || {});
            const out = {};
            Object.keys(params).forEach((k) => {
                const v = params[k];
                if (v !== null && v !== undefined && v !== "" && v !== 0) out[k] = v;
            });
            return out;
        },
        carica() {
            if (!this.mese) return;
            this.caricamento = true;
            this.errore = "";
            /* utils.get = GET esplicito: con utils.ajax (POST) il middleware
               permessi deriverebbe DASHBOARD_CREATE -> 403. */
            global.utils.get("theb/dashboard/manager/heatmap/month",
                this.parametri({ mese: this.mese }),
                (res) => {
                    this.caricamento = false;
                    /* Confronto LOOSE: l'envelope restituisce esito come "0" (stringa). */
                    if (res && res.esito == 0 && res.data) {
                        this.dati = res.data;
                    } else {
                        this.dati = null;
                        this.errore = (res && res.messaggio) ? res.messaggio : "Dati del mese non disponibili.";
                    }
                }, false);
        },
        /* Export INVARIATO (report 1041): nessuna riscrittura. */
        esporta(formato) {
            const p = this.parametri({ mese: this.mese, metrica: this.metrica });
            const qs = Object.keys(p).map((k) => encodeURIComponent(k) + "=" + encodeURIComponent(p[k])).join("&");
            window.open(global.utils.BASE_API + "theb/report?ID_REPORT=1041&FORMATO=" + formato + "&" + qs
                + global.utils.getSessionToken(), "_blank");
        },
    },
};
</script>
<style scoped>
/* UI-05B - redesign SOLO visivo.
   Obiettivo: eliminare spazio verticale inutile. Tutte le altezze sono
   compatte (nessun px gigante); la tabella ha un'area scrollabile PROPRIA
   con max-height relativo al viewport, quindi la modale non cresce con i
   progetti e restano ~5 righe visibili. */

/* ---- contenitore: header/footer fissi, body gestibile ---- */
.ui07-modal .modal-content {
    max-height: 92vh;
    display: flex;
    flex-direction: column;
}
.ui07-modal .modal-header,
.ui07-modal .modal-footer {
    flex-shrink: 0;
}
.ui07-body {
    flex: 1 1 auto;
    overflow: hidden;           /* lo scroll lo gestisce la tabella, non il body */
    padding: 14px 18px;
}

/* ---- 1. HEADER: testata colorata (UI-05C) ----
   Geometria invariata (stesso padding 10px 16px, stessa altezza): cambia
   solo il fondo e il colore dei testi, che diventano bianchi. */
.ui07-header {
    padding: 10px 16px;
    align-items: center;
    background-color: #1f4e9c;          /* petrolio/primary del progetto */
    border-bottom: 1px solid #173f80;
}
.ui07-header-riga {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 10px;
    min-width: 0;
}
.ui07-titolo {
    font-size: 1.02rem;
    font-weight: 700;
    line-height: 1.2;
    color: #fff;
}
.ui07-pill {
    font-size: .7rem;
    font-weight: 600;
    text-transform: uppercase;
    letter-spacing: .03em;
    padding: 2px 9px;
    border-radius: 999px;
    background-color: rgba(255, 255, 255, .22);
    color: #fff;
    border: 1px solid rgba(255, 255, 255, .38);
    white-space: nowrap;
}
.ui07-periodo {
    font-size: .78rem;
    font-weight: 400;
    color: rgba(255, 255, 255, .88);
    padding-left: 10px;
    border-left: 1px solid rgba(255, 255, 255, .35);
    white-space: nowrap;
}

/* ---- 2. KPI: dimensioni INVARIATE, solo gerarchia cromatica (UI-05C) ---- */
.ui07-kpi-row {
    margin-bottom: 10px;
}
.ui07-kpi {
    height: 100%;
    padding: 9px 12px;                  /* invariato */
    border: 1px solid #e9edf2;
    border-left: 3px solid #94a3b8;
    border-radius: 8px;
    background-color: #fbfcfe;
}
.ui07-kpi-riga {
    display: flex;
    align-items: center;
    gap: 6px;
    margin-bottom: 2px;
}
.ui07-kpi-icona {
    font-size: .72rem;
    line-height: 1;
}
.ui07-kpi-label {
    font-size: .62rem;
    font-weight: 700;
    letter-spacing: .04em;
    text-transform: uppercase;
    color: #94a3b8;
    white-space: nowrap;
}
/* Il VALORE resta DARK per massima leggibilita': il colore guida la lettura,
   non sostituisce il testo. */
.ui07-kpi-valore {
    font-size: .98rem;
    font-weight: 700;
    color: #1f2d3d;
    line-height: 1.15;
    overflow-wrap: anywhere;
}
/* tinte semantiche: fondo soft (~7% del colore), bordo e icona pieni,
   label nella stessa tinta ma attenuata. */
.ui07-t-blu { background-color: #f2f7ff; border-color: #d5e4fb; border-left-color: #2563eb; }
.ui07-t-verde { background-color: #f1fbf4; border-color: #d3f0dd; border-left-color: #16a34a; }
.ui07-t-arancio { background-color: #fff7f0; border-color: #fbe0cb; border-left-color: #ea580c; }
.ui07-t-viola { background-color: #f7f4ff; border-color: #e2d9fb; border-left-color: #7c3aed; }
.ui07-t-teal { background-color: #f0fbf9; border-color: #cfeee9; border-left-color: #0d9488; }
.ui07-t-slate { background-color: #f7f9fb; border-color: #e2e8f0; border-left-color: #475569; }
.ui07-t-blu .ui07-kpi-icona, .ui07-t-blu .ui07-kpi-label { color: #2563eb; }
.ui07-t-verde .ui07-kpi-icona, .ui07-t-verde .ui07-kpi-label { color: #16a34a; }
.ui07-t-arancio .ui07-kpi-icona, .ui07-t-arancio .ui07-kpi-label { color: #c2410c; }
.ui07-t-viola .ui07-kpi-icona, .ui07-t-viola .ui07-kpi-label { color: #6d28d9; }
.ui07-t-teal .ui07-kpi-icona, .ui07-t-teal .ui07-kpi-label { color: #0f766e; }
.ui07-t-slate .ui07-kpi-icona, .ui07-t-slate .ui07-kpi-label { color: #475569; }
/* metrica selezionata: leggibile, MA non un bottone (nessun hover/cursore). */
.ui07-kpi-attivo {
    background-color: #e8f0fd;
    border-color: #bcd2f6;
    box-shadow: 0 1px 3px rgba(31, 78, 156, .10);
}
.ui07-kpi-attivo .ui07-kpi-valore {
    color: #12263f;
}

/* ---- 3. CONFRONTO: card bassa (geometria invariata) ---- */
.ui07-confronto {
    border: 1px solid #e9edf2;
    border-radius: 8px;
    padding: 8px 12px 10px;             /* invariato */
    background-color: #fbfcfe;
    margin-bottom: 10px;
}
.ui07-confronto-riga {
    margin-bottom: 0;
}
.ui07-blocco-titolo {
    font-size: .62rem;
    font-weight: 700;
    letter-spacing: .04em;
    text-transform: uppercase;
    color: #1f4e9c;                     /* primary al posto del grigio */
    margin-bottom: 4px;
}
.ui07-micro-label {
    font-size: .62rem;
    text-transform: uppercase;
    letter-spacing: .03em;
    color: #94a3b8;
}
.ui07-micro-valore {
    font-size: .92rem;
    font-weight: 700;
    color: #1f2d3d;
    line-height: 1.2;
}
/* ---- 5. Variazione: semantica cromatica + fondo soft ---- */
.ui07-var {
    display: inline-block;
    font-size: .92rem;
    font-weight: 700;
    line-height: 1.2;
    padding: 1px 8px;
    border-radius: 6px;
}
.ui07-positivo { color: #15803d; background-color: #effaf3; border: 1px solid #cfeedd; }
.ui07-negativo { color: #b91c1c; background-color: #fdf0f0; border: 1px solid #f6d5d5; }
.ui07-neutro { color: #475569; background-color: #f1f5f9; border: 1px solid #e2e8f0; }
/* ---- 5b. INFO BOX: precedente = 0, percentuale non calcolabile ---- */
.ui07-infobox {
    display: flex;
    align-items: flex-start;
    gap: 6px;
    font-size: .72rem;
    line-height: 1.25;
    color: #1e40af;
    background-color: #eef4ff;
    border: 1px solid #d6e4ff;
    border-radius: 6px;
    padding: 3px 8px;
}
.ui07-infobox-icona {
    color: #2563eb;
    line-height: 1.35;
}

/* ---- 4. COMPOSIZIONE ---- */
.ui07-composizione {
    display: flex;
    align-items: baseline;
    flex-wrap: wrap;
    gap: 10px;
    margin-bottom: 6px;
}
.ui07-composizione-meta {
    font-size: .78rem;
    color: #475569;
}
.ui07-link-progetti {
    font-size: .75rem;
    color: #1f4e9c;
    margin-left: auto;
}

/* ---- 5-6. TABELLA: area scrollabile propria, sticky thead e totale ---- */
.ui07-tabella-wrap {
    max-height: min(46vh, 420px);   /* ~5 righe visibili, cresce col viewport */
    overflow-y: auto;
    border: 1px solid #e9edf2;
    border-radius: 8px;
}
.ui07-tabella {
    font-size: .82rem;
    margin-bottom: 0;
}
.ui07-col-idx {
    width: 2.2rem;
    text-align: center;
}
.ui07-progetto {
    font-weight: 700;              /* nome PRIMA, in evidenza */
    color: #1f2d3d;
    line-height: 1.2;
}
.ui07-codice {
    font-size: .72rem;
    color: #94a3b8;                 /* codice SECONDARIO, muted */
    line-height: 1.2;
}
.ui07-stato {
    display: inline-block;
    padding: 1px 8px;
    border-radius: 999px;
    background-color: #f1f5f9;
    color: #475569;
    font-size: .72rem;
    white-space: nowrap;
}
/* Stato chiuso: pill verde soft + testo verde scuro (UI-05C).
   Si applica SOLO a etichette realmente note (COMPLETATO, CHIUSO): nessun
   mapping inventato per stati non conosciuti, che restano neutro. */
.ui07-stato-chiuso {
    background-color: #effaf3;
    color: #15803d;
    border: 1px solid #cfeedd;
}
/* ---- 6. HEADER TABELLA: azzurro molto chiaro (non blu saturo) ---- */
.ui07-tabella thead th {
    position: sticky;
    top: 0;
    z-index: 2;
    background-color: #eef4fc;
    color: #334155;
    font-size: .66rem;
    font-weight: 700;
    text-transform: uppercase;
    letter-spacing: .03em;
    border-bottom: 1px solid #dbe6f5;
    padding: 7px 8px;
    white-space: nowrap;
}
/* ---- 7. COLONNA della metrica selezionata: evidenziazione soft ---- */
.ui07-col-attiva { background-color: #f2f7ff; }
.ui07-tabella thead th.ui07-col-attiva { background-color: #dde9fb; color: #1f4e9c; }
.ui07-col-blu { background-color: #f2f7ff; }
.ui07-col-verde { background-color: #f1fbf4; }
.ui07-col-arancio { background-color: #fff7f0; }
.ui07-col-viola { background-color: #f7f4ff; }
.ui07-tabella thead th.ui07-col-blu { background-color: #dbe9fd; color: #1d4ed8; }
.ui07-tabella thead th.ui07-col-verde { background-color: #dcf3e4; color: #15803d; }
.ui07-tabella thead th.ui07-col-arancio { background-color: #fde8d3; color: #c2410c; }
.ui07-tabella thead th.ui07-col-viola { background-color: #e8dffd; color: #6d28d9; }
.ui07-tabella tbody tr:hover td.ui07-col-attiva,
.ui07-tabella tbody tr:hover th.ui07-col-attiva { background-color: #e6efff; }
.ui07-tabella tbody th,
.ui07-tabella tbody td {
    padding: 5px 8px;
}
/* ---- 9. RIGA TOTALE: ben staccata, senza aumentarne l'altezza ---- */
.ui07-tabella tfoot td {
    position: sticky;
    bottom: 0;                     /* totale sempre visibile */
    z-index: 2;
    background-color: #eef2f8;
    border-top: 2px solid #94a3b8;  /* separazione piu' evidente */
    padding: 7px 8px;
    font-size: .82rem;
}
.ui07-totale {
    font-weight: 700;
    color: #12263f;
}
.ui07-totale-label {
    text-transform: uppercase;
    font-size: .66rem;
    letter-spacing: .04em;
    color: #334155;
}

/* ---- 11. FOOTER: allineato al primary dell'header, geometria invariata ---- */
.ui07-footer {
    padding: 8px 16px;
    justify-content: flex-end;
    gap: 6px;
    background-color: #f8fafc;
    border-top: 1px solid #e9edf2;
}
.ui07-btn {
    padding: 4px 12px;
    font-size: .78rem;
}

/* ---- 10. RESPONSIVE ---- */
@media (max-width: 1199.98px) {
    .ui07-tabella-wrap { max-height: min(52vh, 460px); }
}
@media (max-width: 767.98px) {
    .ui07-header-riga { gap: 6px; }
    .ui07-periodo { padding-left: 0; border-left: 0; white-space: normal; }
    .ui07-body { padding: 12px; }
    .ui07-tabella-wrap { max-height: 55vh; }
}
</style>
